// `/console/account` — S2a, the signed-in staff member's own account.
//
// It was the console's landing until the fleet overview (S3) took `/console`
// (D-626). It shows only what `GET /v1/platform/me` returns (who you are,
// your role, your staff id, whether two-factor is confirmed), the session
// policy the guard chain enforces, and the one account action §5 gives: a
// recovery-code reissue. Sign-out lives in the shell's top bar, on every
// screen, so this screen has none of its own.
//
// WHY THE REISSUE IS HERE AND NOT "LATER". The ten codes are shown exactly
// once, at the end of enrolment, and the acknowledgement checkbox gates only
// the Continue button — back, reload, a typed URL and a closed tab all leave
// that screen, and the codes are gone with it. Without a reissue anywhere in
// the product, a bootstrap admin (who by construction is the only platform
// staff member, with nobody to re-invite them) who clicked past it is one lost
// phone away from a console nobody can reach. `POST auth/recovery-codes/
// regenerate` already existed on the API with no caller. It has one now.
//
// The reissue REQUIRES a fresh authenticator code — that is the endpoint's own
// rule (§5), and it is the right one: a borrowed, still-warm session must not
// be able to rotate the account's last resort out from under its owner. It is
// also why this can never be the answer for someone who has ALREADY lost the
// phone; the codes are, which is what the screen says while there is still time
// to save them.
//
// The session facts are labelled as POLICY, not as a live countdown: the API
// returns no issued-at or idle-deadline, so a ticking timer here would be a
// fabrication. What it says (60 minutes idle, 12 hours absolute) is the
// PlatformSessionGuard's actual rule (§2.13).

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { PageHeader } from "../../../components/breadcrumbs";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { StatusChip } from "../../../components/status-chip";
import { RecoveryCodes } from "../components/recovery-codes";
import { useRoleLabel } from "../console/console-shell";
import {
  classifyTotpCheckFailure,
  regenerateRecoveryCodes,
  type PlatformMe,
  type TotpCheckFailure,
} from "../lib/platform-api";
import {
  useForgetPlatformSession,
  useMarkRecoveryCodesReissued,
} from "../hooks/use-platform-session";
import { setAdminFlash } from "../lib/flash";

/** The 6 digits `PlatformRecoveryRegenerateRequest` accepts. */
const TOTP_SHAPE = /^\d{6}$/u;

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="admin-fact">
      <span className="admin-fact-k">{label}</span>
      <span className="admin-fact-v">{children}</span>
    </div>
  );
}

/**
 * The sentence a failed reissue earns — keyed off WHAT THE FAILURE PROVES
 * (probed 2026-08-17, the unknown-bad-broken audit). Every non-shape failure
 * used to collapse into "That code was not accepted. Wait for the next one and
 * try again." — including a dead network the request never crossed and an
 * expired session nothing about the code caused. The operator reading that
 * checks their phone's clock, re-types the next code, fails again, and starts
 * doubting the enrolment itself; the honest answers are "nothing judged your
 * code — try the same one" and "your session died — sign in", and they are
 * different answers.
 *
 *   shape           — the contract refused six-digits-shaped input before the
 *                     service ran; say what the field wants.
 *   rejected        — the server's verdict on the digits, and the ONE case the
 *                     collapsed no-oracle sentence exists for: wrong, stale and
 *                     replayed keep reading identically so the banner never
 *                     describes the TOTP window to a guesser.
 *   session_expired — rendered for the frame before onError lands on /login,
 *                     and left standing if that navigation ever fails.
 *   broken          — transport / 5xx: nothing was judged and nothing minted;
 *                     retrying the same code is correct and costs nothing.
 */
function reissueErrorKey(failure: TotpCheckFailure): string {
  switch (failure.kind) {
    case "shape":
      return "admin.reissue.format";
    case "rejected":
      return "admin.reissue.failed";
    case "session_expired":
      return "admin.reissue.sessionExpired";
    case "broken":
      return "admin.reissue.notChecked";
  }
}

/** idle → asking for a fresh code → the new batch on the glass. */
type Reissue = { kind: "idle" } | { kind: "asking" } | { kind: "issued"; codes: readonly string[] };

export function AdminAccountScreen({ me }: { me: PlatformMe }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const forgetSession = useForgetPlatformSession();
  const markReissued = useMarkRecoveryCodesReissued();
  // Translated when the dictionary knows the role, the raw code otherwise
  // (the API and the bundle deploy apart; probed 2026-08-17).
  const roleLabel = useRoleLabel(me.role);

  const [reissue, setReissue] = useState<Reissue>({ kind: "idle" });
  const [code, setCode] = useState("");

  const regenerate = useMutation({
    mutationFn: () => regenerateRecoveryCodes(code.trim()),
    onError: (error: unknown) => {
      // A 401 with no TOTP verdict means the SESSION died before the service
      // saw the code; every further action would 401 the same way, so the
      // operator goes to sign in with the reason rather than being told about
      // a code nothing judged.
      if (classifyTotpCheckFailure(error).kind !== "session_expired") return;
      forgetSession();
      setAdminFlash("session_expired");
      void navigate({ to: "/login" });
    },
    onSuccess: (codes) => {
      setCode("");
      setReissue({ kind: "issued", codes });
      // M15-9: the low-codes notice reads the count from the session cache.
      markReissued(codes.length);
    },
  });

  // Within the VERDICT class every refusal reads the same (wrong, stale and
  // replayed are all "not accepted"), so the banner never describes the TOTP
  // window to a guesser; reissueErrorKey above keeps the classes apart.
  const regenerateFailure =
    regenerate.error === null ? undefined : classifyTotpCheckFailure(regenerate.error);

  if (reissue.kind === "issued") {
    return (
      <section className="admin-screen" data-testid="admin-recovery">
        <PageHeader
          title={t("admin.reissue.doneTitle")}
          crumbs={[{ label: t("admin.home.title"), to: "/console/account" }]}
        />
        <Card>
          <div className="admin-card-body">
            <p className="admin-section-b">{t("admin.reissue.doneLead")}</p>
            <RecoveryCodes
              codes={reissue.codes}
              doneLabel={t("admin.reissue.back")}
              onDone={() => {
                setReissue({ kind: "idle" });
              }}
            />
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-account">
      <PageHeader title={t("admin.home.title")} lede={t("admin.home.lead")} />

      <div className="admin-account-grid">
        <Card title={t("admin.account.identity")}>
          <div className="admin-facts admin-facts-grid" data-testid="admin-account-facts">
            <Fact label={t("admin.home.email")}>
              <span data-testid="admin-account-email">{me.email}</span>
            </Fact>
            <Fact label={t("admin.home.role")}>
              <span data-testid="admin-account-role">{roleLabel}</span>
            </Fact>
            <Fact label={t("admin.home.staffId")}>
              <code>{me.id}</code>
            </Fact>
            <Fact label={t("admin.home.twoFactor")}>
              {/* Read from the answer, never asserted: the guard lets only a
                  confirmed member here, but a chip that said so regardless
                  would be a claim this component did not check. */}
              {me.totp_confirmed ? (
                <StatusChip variant="ok">{t("admin.home.twoFactorOn")}</StatusChip>
              ) : (
                <StatusChip variant="warn">{t("admin.home.twoFactorOff")}</StatusChip>
              )}
            </Fact>
            <Fact label={t("admin.home.session")}>{t("admin.home.sessionPolicy")}</Fact>
          </div>
        </Card>

        <Card title={t("admin.reissue.title")} data-testid="admin-reissue">
          <div className="admin-card-body">
            <p className="admin-section-b">{t("admin.reissue.body")}</p>

            {reissue.kind === "asking" ? (
              <>
                {regenerateFailure !== undefined ? (
                  <Banner kind="bad" data-testid="admin-reissue-error">
                    {t(reissueErrorKey(regenerateFailure))}
                    {/* The request id rides along when the server answered
                        with one (a 5xx carries it; a dead socket has none). */}
                    {regenerateFailure.kind === "broken" &&
                    regenerateFailure.requestId !== undefined ? (
                      <span className="admin-reqid">
                        {" "}
                        {t("admin.reissue.requestId")}: <code>{regenerateFailure.requestId}</code>
                      </span>
                    ) : null}
                  </Banner>
                ) : null}
                <form
                  className="admin-form admin-form-flush"
                  data-testid="admin-reissue-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!TOTP_SHAPE.test(code.trim())) return;
                    regenerate.mutate();
                  }}
                >
                  <div className="ds-field">
                    <label htmlFor="admin-reissue-code">{t("admin.reissue.code")}</label>
                    <input
                      id="admin-reissue-code"
                      className="ds-input admin-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      spellCheck={false}
                      maxLength={6}
                      value={code}
                      data-testid="admin-reissue-code"
                      onChange={(e) => {
                        setCode(e.target.value);
                      }}
                      required
                    />
                    <p className="admin-hint">{t("admin.reissue.codeHint")}</p>
                  </div>
                  <div className="admin-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      busy={regenerate.isPending}
                      disabled={!TOTP_SHAPE.test(code.trim())}
                      data-testid="admin-reissue-submit"
                    >
                      {regenerate.isPending
                        ? t("admin.reissue.working")
                        : t("admin.reissue.confirm")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setCode("");
                        regenerate.reset();
                        setReissue({ kind: "idle" });
                      }}
                      data-testid="admin-reissue-cancel"
                    >
                      {t("admin.reissue.cancel")}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="admin-actions">
                <Button
                  onClick={() => {
                    setReissue({ kind: "asking" });
                  }}
                  data-testid="admin-reissue-start"
                >
                  {t("admin.reissue.start")}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
}
